import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  type DashboardTrack,
  useAdminUpdateTrack,
  useMe,
  usePublishStatus,
  useUnpublishStatus,
  useUpsertStatus,
  type WeeklyStatus,
} from "@/lib/api";
import { logger } from "@/lib/logger";
import { phaseLabel, stageBadgeClass } from "@/lib/phase";
import { ragBadgeClass, ragLabel } from "@/lib/rag";
import { cn } from "@/lib/utils";

import {
  InlineSelect,
  InlineTextarea,
  type Option,
  PROGRAM_ROW_ACCENT,
  ProgramTag,
  splitByKind,
  STAGE_OPTIONS,
  trackScalarVars,
  weekStatusBody,
} from "./inline";

/** RAG options for the inline weekly-status selector (labels via ragLabel). */
const RAG_OPTIONS: Option[] = ["green", "amber", "red"].map((value) => ({
  value,
  label: ragLabel(value),
}));

interface WeeklyPulseProps {
  tracks: DashboardTrack[];
  statusesByTrack: Record<string, WeeklyStatus[]>;
  /** Monday of the current week — used when a track has no status yet. */
  thisMonday: string;
  editMode: boolean;
}

/** Split a multi-line text field into trimmed, non-empty bullet lines. */
function toBullets(text: string | null | undefined): string[] {
  if (!text) {
    return [];
  }
  return text
    .split("\n")
    .map((line) => line.replace(/^[-•*]\s*/, "").trim())
    .filter(Boolean);
}

/** The most recent status (by `week_start`) for a track, or undefined. */
function latestStatus(statuses: WeeklyStatus[] | undefined): WeeklyStatus | undefined {
  if (!statuses || statuses.length === 0) {
    return undefined;
  }
  return statuses.reduce((latest, s) => (s.week_start > latest.week_start ? s : latest));
}

interface PulseCellProps {
  bullets: string[];
  highlighted?: boolean;
  editMode: boolean;
  fieldValue: string;
  onSaveField: (value: string) => void;
}

function PulseCell({ bullets, highlighted, editMode, fieldValue, onSaveField }: PulseCellProps) {
  return (
    <div
      className={cn(
        "border-border border-l p-3 first:border-l-0",
        highlighted && "bg-primary/[0.03]",
      )}
    >
      {editMode ? (
        <InlineTextarea
          value={fieldValue}
          rows={3}
          placeholder="One bullet per line"
          onSave={onSaveField}
        />
      ) : bullets.length > 0 ? (
        <ul className="text-muted-foreground list-disc space-y-1 pl-4 text-xs leading-relaxed">
          {bullets.map((b, i) => (
            <li key={i}>{b}</li>
          ))}
        </ul>
      ) : (
        <p className="text-muted-foreground/70 text-xs italic">No update</p>
      )}
    </div>
  );
}

interface BlockerItem {
  track: string;
  text: string;
}

type StatusField = "summary" | "progress" | "nextSteps" | "blockers";

export function WeeklyPulse({ tracks, statusesByTrack, thisMonday, editMode }: WeeklyPulseProps) {
  const upsert = useUpsertStatus();
  const updateTrack = useAdminUpdateTrack();
  const { data: me } = useMe();
  const isAdmin = me?.role === "admin";
  const publish = usePublishStatus();
  const unpublish = useUnpublishStatus();

  // Program lane(s) pin to the top; champion BU rows follow.
  const { programTracks, championTracks } = splitByKind(tracks);

  // Save the current week's RAG on its own status body (full-replace upsert).
  const saveRag = (track: DashboardTrack, status: WeeklyStatus | undefined, rag: string) => {
    const weekStart = status?.week_start ?? thisMonday;
    logger.debug("[dashboard]", "pulse rag", track.id, weekStart, rag);
    upsert.mutate({ ...weekStatusBody(track.id, weekStart, status), rag });
  };

  // Save the track's stage via the same full-scalar track update the pipeline uses.
  const saveStage = (track: DashboardTrack, stage: string) => {
    logger.debug("[dashboard]", "pulse stage", track.id, stage);
    updateTrack.mutate({ ...trackScalarVars(track), stage });
  };

  const saveField = (
    track: DashboardTrack,
    status: WeeklyStatus | undefined,
    field: StatusField,
    value: string,
  ) => {
    // Edit the track's latest status, or create one for the current week.
    const weekStart = status?.week_start ?? thisMonday;
    logger.debug("[dashboard]", "pulse field", track.id, weekStart, field);
    upsert.mutate({
      ...weekStatusBody(track.id, weekStart, status),
      [field]: value.trim() || null,
    });
  };

  // Gather latest-status blockers and at-risk tracks for the bottom cards.
  const blockers: BlockerItem[] = [];
  const decisions: BlockerItem[] = [];
  for (const track of tracks) {
    const status = latestStatus(statusesByTrack[track.id]);
    for (const text of toBullets(status?.blockers)) {
      blockers.push({ track: track.name, text });
    }
    if (track.latest_rag === "red" || track.latest_rag === "amber") {
      decisions.push({
        track: track.name,
        text:
          track.latest_rag === "red"
            ? "Off track — needs steerco decision"
            : "At risk — monitor and support",
      });
    }
  }

  // Publish control (admins only) acts on the CURRENT week's status — matched
  // by `week_start === thisMonday`, not the latest status, so it never touches
  // an older week. No current-week status means nothing to publish yet.
  const renderPublishControl = (track: DashboardTrack) => {
    if (!isAdmin) {
      return null;
    }
    const current = statusesByTrack[track.id]?.find((s) => s.week_start === thisMonday);
    if (!current) {
      return null;
    }
    if (current.state === "draft" || current.state === "in_review") {
      return (
        <Button
          size="sm"
          variant="outline"
          className="h-7 w-fit px-2 text-xs"
          disabled={publish.isPending}
          onClick={() => publish.mutate({ statusId: current.id, trackId: track.id })}
        >
          {publish.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "Publish"}
        </Button>
      );
    }
    if (current.state === "published") {
      return (
        <Button
          size="sm"
          variant="outline"
          className="h-7 w-fit px-2 text-xs"
          disabled={unpublish.isPending}
          onClick={() => unpublish.mutate({ statusId: current.id, trackId: track.id })}
        >
          {unpublish.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "Unpublish"}
        </Button>
      );
    }
    return null;
  };

  const renderRow = (track: DashboardTrack, isProgram: boolean) => {
    const status = latestStatus(statusesByTrack[track.id]);

    return (
      <div
        key={track.id}
        className={cn(
          "border-border grid grid-cols-[160px_1fr_1fr_1fr] border-b last:border-b-0",
          isProgram && PROGRAM_ROW_ACCENT,
        )}
      >
        <div
          className={cn(
            "border-border flex flex-col justify-center gap-1.5 border-r p-3",
            isProgram ? "bg-primary/10" : "bg-muted/40",
          )}
        >
          {isProgram && <ProgramTag />}
          <span className="text-foreground text-lg font-normal">
            {isProgram ? track.name : (track.business_unit ?? track.name)}
          </span>
          {editMode ? (
            <div className="flex flex-col gap-1">
              <InlineSelect
                value={status?.rag ?? track.latest_rag ?? "green"}
                options={RAG_OPTIONS}
                onSave={(v) => saveRag(track, status, v)}
                className="w-32"
              />
              <InlineSelect
                value={track.stage ?? "not_started"}
                options={STAGE_OPTIONS}
                onSave={(v) => saveStage(track, v)}
                className="w-32"
              />
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-1">
              <span className={cn(ragBadgeClass(track.latest_rag ?? "green"))}>
                {ragLabel(track.latest_rag ?? "green")}
              </span>
              <span className={stageBadgeClass(track.stage)}>{phaseLabel(track.stage)}</span>
            </div>
          )}
          {renderPublishControl(track)}
        </div>
        <PulseCell
          editMode={editMode}
          bullets={toBullets(status?.summary)}
          fieldValue={status?.summary ?? ""}
          onSaveField={(v) => saveField(track, status, "summary", v)}
        />
        <PulseCell
          editMode={editMode}
          highlighted
          bullets={toBullets(status?.progress)}
          fieldValue={status?.progress ?? ""}
          onSaveField={(v) => saveField(track, status, "progress", v)}
        />
        <PulseCell
          editMode={editMode}
          bullets={toBullets(status?.next_steps)}
          fieldValue={status?.next_steps ?? ""}
          onSaveField={(v) => saveField(track, status, "nextSteps", v)}
        />
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="border-border overflow-hidden rounded-md border">
        {/* Header row */}
        <div className="border-border bg-muted/40 grid grid-cols-[160px_1fr_1fr_1fr] border-b">
          <div className="border-border border-r p-3" />
          <div className="border-border border-r p-3">
            <p className="text-foreground text-sm">General status</p>
          </div>
          <div className="border-border bg-primary/[0.04] border-r p-3">
            <p className="text-foreground text-sm">What we&apos;re focused on</p>
          </div>
          <div className="p-3">
            <p className="text-foreground text-sm">What&apos;s coming up</p>
          </div>
        </div>

        {/* Program lane(s) pinned first, then one row per champion track */}
        {programTracks.map((track) => renderRow(track, true))}
        {championTracks.map((track) => renderRow(track, false))}
      </div>

      {/* Bottom cards */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="border-border bg-card rounded-md border p-4">
          <p className="text-foreground mb-3 text-[10px] font-medium uppercase">
            Decisions needed from steerco
          </p>
          {decisions.length > 0 ? (
            <ul className="space-y-2 text-xs leading-relaxed">
              {decisions.map((d, i) => (
                <li key={i} className="text-muted-foreground">
                  <span className="text-foreground font-medium">{d.track}:</span> {d.text}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted-foreground/70 text-xs italic">No decisions pending.</p>
          )}
        </div>
        <div className="border-rag-red bg-card rounded-md border border-l-4 p-4">
          <p className="text-rag-red mb-3 text-[10px] font-medium uppercase">
            Risks &amp; blockers
          </p>
          {editMode ? (
            <div className="space-y-3">
              {tracks.map((track) => {
                const status = latestStatus(statusesByTrack[track.id]);
                return (
                  <div key={track.id} className="space-y-1">
                    <p className="text-foreground text-sm font-normal">
                      {track.business_unit ?? track.name}
                    </p>
                    <InlineTextarea
                      value={status?.blockers ?? ""}
                      rows={2}
                      placeholder="One blocker per line"
                      onSave={(v) => saveField(track, status, "blockers", v)}
                    />
                  </div>
                );
              })}
            </div>
          ) : blockers.length > 0 ? (
            <ul className="space-y-2 text-xs leading-relaxed">
              {blockers.map((b, i) => (
                <li key={i} className="text-muted-foreground">
                  <span className="text-foreground font-medium">{b.track}:</span> {b.text}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted-foreground/70 text-xs italic">No blockers reported.</p>
          )}
        </div>
      </div>
    </div>
  );
}
